/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  forwardRef,
  useContext,
  type CSSProperties,
  type ElementType,
  type KeyboardEvent,
} from "react";

import { cn, flattenStyles, platformProps } from "../../lib";
import { ariaLabelFor } from "../../lib/aria-name";
import { getHeadingLevel } from "../../lib/aria-state";
import type { TextAccessibilityRole, TextProps } from "./types";

/**
 * Context for passing default text classes down the tree.
 * Useful for theming text within specific containers.
 */
export const TextClassContext = createContext<string | undefined>(undefined);

const ROLE_MAP: Record<TextAccessibilityRole, string | undefined> = {
  none: "presentation",
  // ARIA has no "text" role; the element stays generic and its content is read as-is
  text: undefined,
  header: "heading",
  link: "link",
  alert: "alert",
  button: "button",
};

/**
 * Text - Web implementation
 *
 * Polymorphic text wrapper that renders semantic HTML elements.
 * Transforms accessibility props to ARIA attributes.
 *
 * Features:
 * - TextClassContext for inherited styling
 * - Smart `as` defaulting (label when htmlFor is provided)
 * - numberOfLines via CSS line-clamp
 * - Selectable by default on web
 * - Keyboard-operable when `onClick` is set (focusable, role button or link)
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      children,
      className,
      testID,
      as,
      accessibilityLabel,
      accessibilityHint,
      accessibilityRole,
      numberOfLines,
      // Native-only (the phone's text size); taken out so it doesn't reach the DOM
      maxFontSizeMultiplier: _maxFontSizeMultiplier,
      selectable = true,
      htmlFor,
      onClick,
      style,
      ...rest
    },
    ref,
  ) => {
    const textClass = useContext(TextClassContext);

    // Smart default: use label when htmlFor is provided
    const element = as ?? (htmlFor ? "label" : "span");
    const Component = element as ElementType;

    // CSS for line clamping (numberOfLines)
    const lineClampStyle: CSSProperties | undefined = numberOfLines
      ? {
          display: "-webkit-box",
          WebkitLineClamp: numberOfLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }
      : undefined;

    // User select style
    const selectStyle: CSSProperties | undefined = selectable ? undefined : { userSelect: "none" };

    const combinedStyle: CSSProperties | undefined =
      lineClampStyle || selectStyle || style
        ? { ...lineClampStyle, ...selectStyle, ...(style ? flattenStyles([style]) : undefined) }
        : undefined;

    // Only pass htmlFor to label elements
    const labelProps = element === "label" && htmlFor ? { htmlFor } : {};

    // Clickable text is a tab stop, so it is always a button or a link.
    const role = onClick
      ? accessibilityRole === "link"
        ? "link"
        : "button"
      : accessibilityRole
        ? ROLE_MAP[accessibilityRole]
        : undefined;

    // An explicit `role` in rest wins in the spread below, even `undefined`
    const renderedRole = "role" in rest ? (rest as { role?: string }).role : role;
    // An h1–h6 is already a heading at its tag's level. role="heading" on it is
    // redundant, and with no aria-level checkers (axe) then read it as level 2.
    const ownRole = role === "heading" && /^h[1-6]$/.test(element) ? undefined : role;

    // A parent that brings its own keyboard handling (Pressable asChild) overrides this via `rest`.
    const handleKeyDown = onClick
      ? (e: KeyboardEvent<HTMLElement>) => {
          if (e.key === "Enter" || (e.key === " " && role === "button")) {
            e.preventDefault();
            onClick();
          }
        }
      : undefined;

    return (
      <Component
        ref={ref}
        className={cn(
          "text-luce-neutral-foreground-default text-size-body-lg",
          selectable && "select-text",
          onClick &&
            "focus-visible:ring-luce-brand-primaryBorder cursor-pointer focus-visible:outline-none focus-visible:ring-2",
          textClass,
          className,
        )}
        {...platformProps({ testID })}
        aria-label={ariaLabelFor("Text", accessibilityLabel, renderedRole, element)}
        aria-description={accessibilityHint}
        role={ownRole}
        {...getHeadingLevel(renderedRole, element)}
        tabIndex={onClick ? 0 : undefined}
        style={combinedStyle}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...labelProps}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

Text.displayName = "Text";
